import React, { useEffect, useState } from 'react';
import { Modal } from 'components/Modal';
import { PartnersPicker } from 'components/partners-picker/index';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Color } from 'core';
import { handleDestroySession } from 'store/session';
import { IPartner } from 'models/partner';

interface IAccountSuspensionGuardProps {
  children: React.ReactNode;
  partner?: IPartner;
  usePrimaryPartner: boolean;
}

export const AccountSuspensionGuard = ({
  children,
  partner: propPartner,
  usePrimaryPartner,
}: IAccountSuspensionGuardProps) => {
  const user = useCurrentUser();
  const partner = usePrimaryPartner ? user?.primaryPartner : propPartner;
  const isPartnerAdmin: boolean = partner?.meta?.role === 'admin';
  const [showPartnerPicker, setShowPartnerPicker] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  // Don't show suspension guard for control panel
  const isControlPanel = location.pathname.startsWith('/nav/control-panel');

  const [blocked, setBlocked] = useState(
    !isControlPanel &&
      ((partner?.partnerSettings?.partnerAdminBlockEnabled && isPartnerAdmin) ||
        partner?.partnerSettings?.blockAllAccountAccess)
  );

  useEffect(() => {
    setBlocked(
      !isControlPanel &&
        ((partner?.partnerSettings?.partnerAdminBlockEnabled && isPartnerAdmin) ||
          partner?.partnerSettings?.blockAllAccountAccess)
    );
  }, [partner, isPartnerAdmin, isControlPanel]);

  const handleSignOut = async () => {
    await dispatch(handleDestroySession() as any);
    window.location.href = '/app/signin';
  };

  if (showPartnerPicker) {
    return <Modal show={showPartnerPicker} element={<PartnersPicker onClose={() => setShowPartnerPicker(false)} />} />;
  }

  if (blocked) {
    return (
      <SuspensionModal>
        <SuspensionModalOverlay>
          <SuspensionModalContent>
            <h1>This account is currently suspended</h1>
            <h2>To reactivate this account, please contact your company's Cerego representative.</h2>
            <ButtonContainer>
              <SwitchAccountButton onClick={() => setShowPartnerPicker(true)}>Switch Account</SwitchAccountButton>
              <LogoutButton onClick={handleSignOut}>Sign Out</LogoutButton>
            </ButtonContainer>
          </SuspensionModalContent>
        </SuspensionModalOverlay>
      </SuspensionModal>
    );
  }

  return <>{children}</>;
};

const SuspensionModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999; /* Higher than regular modals */
`;

const SuspensionModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${Color.white};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SuspensionModalContent = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  text-align: center;
  padding: 40px;
  max-width: 600px;
  margin: 20px;

  h1 {
    margin-bottom: 16px;
    color: ${Color.darkGray};
  }

  h2 {
    margin-bottom: 24px;
    color: ${Color.textGray};
    font-weight: normal;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  justify-content: center;
`;

const BaseButton = styled.button`
  background: transparent;
  border: 1px solid;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const SwitchAccountButton = styled(BaseButton)`
  border-color: ${Color.ceregoGreen};
  color: ${Color.ceregoGreen};

  &:hover {
    background: ${Color.ceregoGreen};
    color: white;
  }
`;

const LogoutButton = styled(BaseButton)`
  border-color: ${Color.darkGray};
  color: ${Color.darkGray};

  &:hover {
    background: ${Color.darkGray};
    color: white;
  }
`;
